import { useState } from 'react';
import { useFormContext, useFieldArray, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { InitiativeWriteInput } from '@complan/contracts';
import { Button } from '@/components/ui/button';
import { DateField } from '@/shared/components/DateField';
import { DateRangeField } from '@/shared/components/DateRangeField';
import { Field, TextArea, ControlledSelect, ConditionalBlock } from './fields';
import type { IntakeOptionSets } from './intake-options';
import type { ConditionalSection } from './intake-form-model';
import { IdArrayCheckboxField } from './steps';

type FormValues = InitiativeWriteInput;

/** Step 6 — Milestones: a repeatable list of {type, planned date, completed date} (R5.1). */
export function MilestonesStep({ options }: { readonly options: IntakeOptionSets }): React.JSX.Element {
  const { t } = useTranslation();
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray<FormValues, 'milestones'>({ control, name: 'milestones' });
  return (
    <section>
      <h2 className="mb-3 text-[13px] font-semibold text-txt">{t('intake.milestones.heading')}</h2>
      {fields.length === 0 ? (
        <p className="text-muted-foreground mb-2 text-[12px]">{t('intake.milestones.none')}</p>
      ) : null}
      {fields.map((row, index) => (
        <div key={row.id} className="border-line mb-2 rounded-[var(--radius-control)] border p-2">
          <Field label={t('intake.milestones.typeLabel')}>
            <MilestoneTypeSelect index={index} placeholder={t('intake.milestones.typePlaceholder')} options={options} />
          </Field>
          <Field label={t('intake.milestones.plannedDateLabel')}>
            <MilestoneDateField index={index} field="plannedDate" label={t('intake.milestones.plannedDateLabel')} />
          </Field>
          <Field label={t('intake.milestones.completedDateLabel')}>
            <MilestoneDateField index={index} field="completedDate" label={t('intake.milestones.completedDateLabel')} />
          </Field>
          <Button variant="ghost" size="xs" onClick={() => remove(index)}>
            {t('intake.milestones.remove')}
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          append({ milestoneTypeId: '', plannedDate: '' as FormValues['startDate'], completedDate: null })
        }
      >
        {t('intake.milestones.add')}
      </Button>
    </section>
  );
}

function MilestoneTypeSelect({
  index,
  placeholder,
  options,
}: {
  readonly index: number;
  readonly placeholder: string;
  readonly options: IntakeOptionSets;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { field } = useController<FormValues>({ name: `milestones.${index}.milestoneTypeId` });
  return (
    <ControlledSelect
      options={options.milestoneTypes}
      placeholder={placeholder}
      inactiveMarker={t('intake.inactiveMarker')}
      value={(field.value as string | undefined) ?? ''}
      onChange={(event) => field.onChange(event.target.value)}
    />
  );
}

/** One milestone row's planned- or completed-date field, as a compact `DateField`. */
function MilestoneDateField({
  index,
  field: fieldName,
  label,
}: {
  readonly index: number;
  readonly field: 'plannedDate' | 'completedDate';
  readonly label: string;
}): React.JSX.Element {
  const { field } = useController<FormValues>({ name: `milestones.${index}.${fieldName}` });
  return (
    <DateField
      value={(field.value as string | null | undefined) ?? null}
      onChange={(date) => field.onChange(date)}
      label={label}
    />
  );
}

/**
 * Step 7 — DTE and CSC dependencies (R5.1, R5.3, R5.5). Each function's block is a
 * `.cond` disclosure (UI spec §6.1) shown only while its involvement flag is yes;
 * the block carries the dependency description (required) and the optional
 * dependency owner and due date. The CSC block opens with the CSC attributes, a
 * master-data multi-select (Inbound, Outbound, ...) like the classification step.
 *
 * The dependency rows live in the single `dependencies[]` write array keyed by
 * `dependencyFunction`; toggling involvement is handled by the wizard (which
 * raises the discard confirmation), so this step only edits the fields of a
 * present row.
 */
export function DependenciesStep({
  showDte,
  showCsc,
  options,
}: {
  readonly showDte: boolean;
  readonly showCsc: boolean;
  readonly options: IntakeOptionSets;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className="mb-3 text-[13px] font-semibold text-txt">{t('intake.dependencies.heading')}</h2>
      {showDte ? (
        <ConditionalBlock className="mb-2">
          <h3 className="mb-2 text-[12px] font-semibold text-txt">{t('intake.dependencies.dteBlockHeading')}</h3>
          <DependencyFields fn="dte" options={options} />
        </ConditionalBlock>
      ) : null}
      {showCsc ? (
        <ConditionalBlock>
          <h3 className="mb-2 text-[12px] font-semibold text-txt">{t('intake.dependencies.cscBlockHeading')}</h3>
          <Field label={t('intake.dependencies.cscAttributeLegend')}>
            <IdArrayCheckboxField
              name="cscAttributeIds"
              legend={t('intake.dependencies.cscAttributeLegend')}
              options={options.cscAttributes}
            />
          </Field>
          <DependencyFields fn="csc" options={options} />
        </ConditionalBlock>
      ) : null}
    </section>
  );
}

/**
 * The description/owner/activity-date fields for one dependency function. The
 * activity date is either a single date (`dueDate`) or a period (`dueDate` to
 * `dueEndDate`, migration 33). Finds (or
 * lazily materialises) the row for `fn` in `dependencies[]` and edits it in place,
 * so a hidden function contributes no row until its block is shown.
 */
function DependencyFields({
  fn,
  options,
}: {
  readonly fn: ConditionalSection & ('dte' | 'csc');
  readonly options: IntakeOptionSets;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { field } = useController<FormValues>({ name: 'dependencies' });
  const rows = (field.value as FormValues['dependencies'] | undefined) ?? [];
  const index = rows.findIndex((row) => row.dependencyFunction === fn);
  const row = index >= 0 ? rows[index] : undefined;

  function patch(next: Partial<NonNullable<FormValues['dependencies']>[number]>): void {
    if (index >= 0) {
      const updated = rows.map((existing, i) => (i === index ? { ...existing, ...next } : existing));
      field.onChange(updated);
    } else {
      field.onChange([
        ...rows,
        {
          dependencyFunction: fn,
          description: null,
          ownerText: null,
          dueDate: null,
          dueEndDate: null,
          completedAt: null,
          ...next,
        },
      ]);
    }
  }

  return (
    <>
      <Field label={t('intake.dependencies.descriptionLabel')} required requiredMark={t('intake.requiredMark')}>
        <TextArea
          placeholder={t('intake.dependencies.descriptionPlaceholder')}
          value={row?.description ?? ''}
          onChange={(event) => patch({ description: event.target.value || null })}
        />
      </Field>
      <Field label={t('intake.dependencies.ownerLabel')}>
        <ControlledSelect
          options={options.owners}
          placeholder={t('intake.dependencies.ownerPlaceholder')}
          inactiveMarker={t('intake.inactiveMarker')}
          value={row?.ownerText ?? ''}
          onChange={(event) => patch({ ownerText: event.target.value || null })}
        />
      </Field>
      <ActivityDateField
        fn={fn}
        dueDate={row?.dueDate ?? null}
        dueEndDate={row?.dueEndDate ?? null}
        onChange={(next) => patch(next)}
      />
    </>
  );
}

type DependencyDate = NonNullable<NonNullable<FormValues['dependencies']>[number]['dueDate']>;

/**
 * The dependency's Activity date: a "Single date / Period" choice above either a
 * date picker or a start/end picker. Switching mode keeps the start date, so a
 * single date becomes the start of a period and a period collapses to its start.
 */
function ActivityDateField({
  fn,
  dueDate,
  dueEndDate,
  onChange,
}: {
  readonly fn: 'dte' | 'csc';
  readonly dueDate: DependencyDate | null;
  readonly dueEndDate: DependencyDate | null;
  readonly onChange: (next: { dueDate: DependencyDate | null; dueEndDate: DependencyDate | null }) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [isPeriod, setIsPeriod] = useState(dueEndDate !== null);
  const period = isPeriod || dueEndDate !== null;
  const modes = [
    { value: false, label: t('intake.dependencies.singleDate') },
    { value: true, label: t('intake.dependencies.period') },
  ];
  return (
    <Field label={t('intake.dependencies.dueDateLabel')}>
      <div
        role="radiogroup"
        aria-label={t('intake.dependencies.dateModeLabel')}
        className="mb-1 flex gap-4"
      >
        {modes.map((mode) => (
          <label key={String(mode.value)} className="flex cursor-pointer items-center gap-2 text-[12px] text-txt">
            <input
              type="radio"
              name={`${fn}-activity-date-mode`}
              checked={period === mode.value}
              onChange={() => {
                setIsPeriod(mode.value);
                if (!mode.value) onChange({ dueDate, dueEndDate: null });
              }}
            />
            <span>{mode.label}</span>
          </label>
        ))}
      </div>
      {period ? (
        <DateRangeField
          value={dueDate && dueEndDate ? { startDate: dueDate, endDate: dueEndDate } : null}
          onChange={(range) =>
            onChange({
              dueDate: (range?.startDate ?? null) as DependencyDate | null,
              dueEndDate: (range?.endDate ?? null) as DependencyDate | null,
            })
          }
          label={t('intake.dependencies.periodLabel')}
        />
      ) : (
        <DateField
          value={dueDate}
          onChange={(date) => onChange({ dueDate: date as DependencyDate | null, dueEndDate: null })}
          label={t('intake.dependencies.dueDateLabel')}
        />
      )}
    </Field>
  );
}
